<template>
  <div class="index container">
    <transition name="fade">
      <router-view @showParent="showParent"> </router-view>
    </transition>

    <div v-if="showParentPage">
      <v-breadcrumbs>
        <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
          >Home</v-breadcrumbs-item
        >
        <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
        <v-breadcrumbs-item
          :exact="true"
          :to="{ name: 'student', params: {} }"
          active
        >
          Students
        </v-breadcrumbs-item>
      </v-breadcrumbs>

      <div class="d-flex flex-column flex-md-row">
        <div class="flex-grow-1 mb-5">
          <v-text-field
            dense
            v-model="searchTerms"
            @keyup.enter="search"
            @click:clear="clearSearch"
            @click:append="search"
            placeholder="Press enter to begin search"
            append-icon="mdi-magnify"
            outlined
            clearable
            label="Search by student name"
            hide-details
          />
          <div class="d-flex mt-4">
            <v-select
              outlined
              dense
              multiple
              chips
              small-chips
              :items="classrooms"
              item-value="id"
              item-text="roomNumber"
              clearable
              label="Filter by Classroom"
              v-model="selectedClassrooms"
              @change="search"
            />
            <v-switch
              class="mt-1 ml-4"
              @change="search"
              dense
              hide-details
              v-model="activeOnly"
              label="Active only"
            />
          </div>
        </div>
        <div class="d-flex flex-row">
          <v-select
            label="Filter Status"
            @change="search"
            v-model="requirementStatus"
            class="ml-md-4"
            :style="`${$vuetify.breakpoint.mdAndUp ? 'max-width: 200px;' : ''}`"
            dense
            outlined
            v-if="requirementStatus"
            :items="['All', 'Completed', 'Incomplete']"
          />
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ml-4" color="primary" v-bind="attrs" v-on="on"
                >Create</v-btn
              >
            </template>
            <v-card class="mx-auto" max-width="400" width="200" tile>
              <v-list dense>
                <v-list-item-group color="primary">
                  <v-list-item :to="{ name: 'studentCreate', params: {} }">
                    <v-list-item-content>
                      <v-list-item-title>New Student</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item :to="{ name: 'studentUpload', params: {} }">
                    <v-list-item-content>
                      <v-list-item-title>Upload Students</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-card>
          </v-menu>
        </div>
      </div>

      <div class="table-container">
        <v-data-table
          :headers="headers"
          :items="studentList"
          :items-per-page="pagination.pageSize"
          :loading="!studentProcessed"
          :server-items-length="pagination.totalSize"
          :page="pagination.current"
          :disable-pagination="!studentList || studentList.length < 1"
          :hide-default-header="!studentList || studentList.length < 1"
          :hide-default-footer="!studentList || studentList.length < 1"
          :disable-sort="true"
          item-key="id"
          class="elevation-1"
          :options.sync="options"
          @update:options="pageChanged"
        >
          <template v-slot:item.firstName="{ item }">
            <a @click="view(item)">{{ item.firstName }} {{ item.lastName }}</a>
            <div class="text-caption font-weight-light">
              <span v-if="globalCenter && !globalCenter.id">
                {{ getCenterName(item.centerId) }} -
              </span>
              <span v-if="item.classroomName">
                {{ item.classroomName }} -
              </span>
              <span>
                {{ item.contactTypeName }}
                <span v-if="!item.active"
                  ><v-divider vertical />(Inactive)</span
                >
              </span>
            </div>
          </template>

          <template v-slot:item.requirements="{ item }">
            <v-skeleton-loader v-if="loadingRequirement" type="list-item" />
            <div v-else class="text-subtitle-2 font-weight-light">
              {{ getStudentRequirementStatus(item.id) }}
            </div>
          </template>

          <template v-slot:item.medicalAlerts="{ item }">
            <v-skeleton-loader v-if="loadingMedicalAlerts" type="list-item" />
            <div v-else class="text-subtitle-2 font-weight-light">
              <div v-for="(value) in getStudentMedicalAlerts(item.id)" :key="value.id">
                {{value.medicalAlertName}}
                <span v-if="value.medicalAlertOptionId"> - {{value.medicalAlertOptionName}}</span>
              </div>
            </div>
          </template>

          <template v-slot:item.lastUpdate="{ item }">
            {{ getLastUpdate(item.id) | formatDateYear }}
          </template>
        </v-data-table>
      </div>
    </div>
    <confirmation-dialog ref="confirm" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getStudentRequirements } from "@/services/requirement";
import { fetchStudentsMedicalAlerts, groupMedicalAlertsByStudents} from "@/services/medicalAlertServices";
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");

export default {
  data() {
    return {
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 100,
      },
      options: {
        //define default sorting option here.
        sortBy: [],
        sortDesc: [],
      },
      classrooms: [],
      selectedClassrooms: [],
      loadingRequirement: true,
      loadingMedicalAlerts: true,
      studentList: [],
      showParentPage: true,
      student2Requirements: new Map(),
      student2LastUpdate: new Map(),
      student2MedicalAlerts: {},
      headers: [
        {
          text: "Name",
          value: "firstName",
          align: "start",
          sortable: true,
        },
        {
          text: "Requirements",
          value: "requirements",
          align: "start",
          sortable: false,
        },
        {
          text: "Medical Alerts",
          value: "medicalAlerts",
          align: "start",
          sortable: false,
        },
        {
          text: "Last Update",
          value: "lastUpdate",
          align: "start",
          sortable: true,
        },
      ],
      sort: {
        key: null,
      },
      searchTerms: "",
    };
  },
  components: {
    ConfirmationDialog,
  },
  computed: {
    ...mapGetters("student", ["studentProcessed", "getStudents"]),
    ...mapGetters("auth", ["myCenters"]),
    ...mapGetters("navigation", ["isAppLoading", "globalCenter"]),

    allRequirementComplete() {
      if (this.requirementStatus == "Completed") {
        return true;
      } else if (this.requirementStatus == "Incomplete") {
        return false;
      } else {
        return null;
      }
    },
    activeOnly: {
      get() {
        return this.$store.state.student.params['activeOnly']
      },
      set(value) {
        this.$store.state.student.params['activeOnly'] = value;
      }
    },
    requirementStatus: {
      get() {
        return this.$store.state.student.params['requirementStatus']
      },
      set(value) {
        this.$store.state.student.params['requirementStatus'] = value;
      }
    }
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("student", ["deleteStudent", "fetchStudents"]),
    ...mapActions("classroom", ["fetchClassrooms"]),
    getClassrooms() {
      //default to sorting by ID desc if not defined
      const sortBy = "roomNumber";
      const sortDesc = false;
      const params = {
        page: 0,
        size: 1000,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
      };
      this.fetchClassrooms(params)
        .then((response) => {
          if (response) {
            this.classrooms = response.content;
          }
        })
        .catch((error) => {
          this.addErrorMessage("Server response with error " + error);
        });
    },
    view(item) {
      this.$router.push({ name: "studentView", params: { id: item.id } });
    },
    getResources() {
      //skip if parent page is not showing...
      if (!this.showParentPage) {
        return;
      }

      this.loadingRequirement = true;
      this.loadingMedicalAlerts = true;
      //default to sorting by ID desc if not defined
      const sortBy =
        this.options["sortBy"] && this.options["sortBy"].length > 0
          ? this.options.sortBy[0]
          : "id";
      const sortDesc =
        this.options["sortDesc"] && this.options["sortDesc"].length > 0
          ? this.options.sortDesc[0]
          : true;

      const params = {
        page:
          this.pagination.pageIndex && this.pagination.pageIndex > 0
            ? this.pagination.pageIndex - 1
            : 0,
        size: this.pagination.pageSize,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
        searchTerms: this.searchTerms
          ? this.searchTerms.replace(/\s+/g, " ").trim()
          : null,
        activeOnly: this.activeOnly,
        allRequirementComplete: this.allRequirementComplete,
      };

      let classroomIds = null;
      if(this.selectedClassrooms && this.selectedClassrooms.length > 0){
        classroomIds = this.selectedClassrooms.join(',');
      }
      params.classroomIds = classroomIds;

      this.fetchStudents(params)
        .then((response) => {
          if (response) {
            this.pagination.totalSize = response.totalElements;
            this.studentList = response.content;

            const studentIds = this.studentList.map((s) => s.id);
            this.loadStudentRequirements(studentIds)
              .then((response) => {
                this.student2Requirements = response;
                this.loadingRequirement = false;
              })
              .catch((error) => {
                console.error(error);
                this.addErrorMessage(
                  "Error loading requirement status for student"
                );
                this.loadingRequirement = false;
              });

            this.loadStudentMedicalAlerts(studentIds);
          }
        })
        .catch((error) => {
          this.loadingRequirement = false;
          this.addErrorMessage("Server response with error " + error);
        });
    },
    showParent(show) {
      this.showParentPage = show;
    },
    pageChanged(pagination) {
      this.pagination.pageIndex = pagination.page;
      this.pagination.pageSize = pagination.itemsPerPage;

      this.getResources();
    },
    search() {
      this.getResources();
    },
    clearSearch() {
      this.searchTerms = null;
      this.getResources();
    },
    async loadStudentRequirements(studentIds) {
      const ids =
        studentIds && studentIds.length > 0 ? studentIds.join(",") : null;
      if (ids) {
        return getStudentRequirements(ids, true).then((response) => {
          const requirements = response.data;
          let student2Requirements = new Map();

          if (requirements && requirements.length > 0) {
            student2Requirements = requirements.reduce((map, r) => {
              const studentId = r.studentId;
              let completedCount = 0;
              let incompleteCount = 0;
              const statuses = r.status ? Object.values(r.status) : [];

              if (r.status) {
                completedCount = statuses.filter(
                  (s) => s.state == "completed" || s.state == "pending"
                ).length;
                incompleteCount = statuses.filter(
                  (s) => s.state == "incomplete"
                ).length;
              }

              if (map.has(studentId)) {
                map.get(studentId).completedCount += completedCount;
                map.get(studentId).incompleteCount += incompleteCount;
                map.get(studentId).requirements.push(r);
              } else {
                map.set(studentId, {
                  requirements: [r],
                  completedCount,
                  incompleteCount,
                });
              }

              // update last updated time student
              const lastUpdate = statuses.reduce((max, s) => {
                if (s.submissionDate) {
                  const lastUpdate = this.parseISOLocalDate(s.submissionDate);
                  if (max == null || (lastUpdate && max < lastUpdate)) {
                    max = lastUpdate;
                  }
                }
                return max;
              }, null);

              const studentLastUpdate = this.student2LastUpdate.get(studentId);
              if (
                studentLastUpdate == null ||
                (lastUpdate != null && studentLastUpdate < lastUpdate)
              ) {
                this.student2LastUpdate.set(studentId, lastUpdate);
              }

              return map;
            }, new Map());
          }

          return student2Requirements;
        });
      } else {
        return null;
      }
    },
    async loadStudentMedicalAlerts(studentIds) {
      const medicalAlerts = await fetchStudentsMedicalAlerts(studentIds);
      this.student2MedicalAlerts = await groupMedicalAlertsByStudents(medicalAlerts);

      this.loadingMedicalAlerts = false;
    },
    getStudentRequirementStatus(studentId) {
      if (this.student2Requirements) {
        const status = this.student2Requirements.get(studentId);
        if (status) {
          const totalCount = status.incompleteCount + status.completedCount;
          return `${status.completedCount} of ${totalCount} requirements completed`;
        } else {
          return "n/a";
        }
      }
    },
    getStudentMedicalAlerts(studentId) {
      if (this.student2MedicalAlerts) {
        const studentAlert = this.student2MedicalAlerts[studentId];
        if (studentAlert) {
          return studentAlert.medicalAlerts;
        } else {
          return [];
        }
      }
    },
    getLastUpdate(studentId) {
      if (this.student2Requirements) {
        return this.student2LastUpdate.get(studentId);
      } else {
        return null;
      }
    },
    parseISOLocalDate(s) {
      var b = s.split(/\D/);
      return new Date(b[0], b[1] - 1, b[2]);
    },
    getCenterName(id) {
      if (this.myCenters && this.myCenters.length > 0) {
        const center = this.myCenters.find((c) => c.id == id);
        return center ? center.name : "Location N/A";
      } else {
        return "Location N/A";
      }
    },
  },
  watch: {
    "pagination.numberRegisterForPage": function () {
      this.pagination.current = -1;
    },
  },
  beforeRouteUpdate(to, from, next) {
    const toDepth = to.path.split("/").length;
    const fromDepth = from.path.split("/").length;
    this.showParentPage = toDepth < fromDepth;
    if (this.showParentPage) {
      this.getResources();
    }
    next();
  },
  mounted() {
    this.getClassrooms();
  },
};
</script>
